import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  MessageSquare,
  Check,
  Mail,
  Copy,
} from "lucide-react";
import { format } from "date-fns";
import { Notification } from "@/pages/notifications/types/notification";
import { Link, useParams } from "react-router-dom";
import { ALL_REGIONS_CODE, regionPath } from "@/features/regions/constants";
import { HasAccess } from "@/components/common/has-access";
import {
  useNotification,
  useSendNotificationAnyway,
} from "../hooks/use-notifications";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

interface NotificationCardProps {
  id: string;
  notification: Notification;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (notification: Notification, checked: boolean) => void;
  hoveredNotificationId?: string | null;
  setHoveredNotificationId?: (id: string | null) => void;
  onStatusChange?: (id: string, newStatus: string) => void;
  onUpdateTags?: (args: { notificationId: string; tags: string[] }) => void;
}

// Mirrors NotificationTemplateService.isCancellationNotification on the server.
// Kept as an explicit list rather than a prefix match so a new
// SESSION_CANCELLATION_* enum value fails CLOSED here until it is also added to
// the server's list; otherwise the button would show and the server would fall
// through to the booking branch (review note on #1862).
const CANCELLATION_NOTIFICATION_TYPES: readonly string[] = [
  "SESSION_CANCELLATION_USER",
  "SESSION_CANCELLATION_PARTNER",
  "SESSION_CANCELLATION_PROFESSIONAL",
];

export const NOTIFICATION_TAG_OPTIONS = [
  { label: "Resolved", value: "Resolved" },
  { label: "Reached Out", value: "Reached Out" },
  { label: "Review", value: "Review" },
];

const TAG_COLOR_MAP: Record<string, string> = {
  Resolved: "bg-green-100 text-green-800",
  "Reached Out": "bg-blue-100 text-blue-800",
  Review: "bg-yellow-100 text-yellow-800",
};

export const getStatusIcon = (status: string) => {
  switch (status) {
    case "SENT":
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case "PENDING":
      return <Clock className="h-4 w-4 text-yellow-600" />;
    case "FAILED":
      return <XCircle className="h-4 w-4 text-red-600" />;
    case "RETRY":
      return <RefreshCw className="h-4 w-4 text-blue-600" />;
    case "HOLD":
      return <Clock className="h-4 w-4 text-slate-600" />;
    default:
      return <Clock className="h-4 w-4 text-gray-600" />;
  }
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "SENT":
      return "bg-green-100 text-green-800";
    case "PENDING":
      return "bg-yellow-100 text-yellow-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    case "RETRY":
      return "bg-blue-100 text-blue-800";
    case "HOLD":
      return "bg-slate-100 text-slate-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case "WHATSAPP":
      return <MessageSquare className="h-4 w-4 text-green-600" />;
    case "EMAIL":
      return <Mail className="h-4 w-4 text-blue-600" />;
    default:
      return <MessageSquare className="h-4 w-4 text-gray-600" />;
  }
};

const formatNotificationType = (type: string) => {
  if (!type) return "UNKNOWN";
  const parts = type.split("_").filter(Boolean);
  const replaced = parts.map((p) => {
    switch (p) {
      case "PROFESSIONAL":
        return "PRO";
      case "CANCELLATION":
        return "CANCEL";
      case "CONFIRMATION":
        return "CONF";
      case "REMINDER":
        return "REM";
      case "TRANSITION":
        return "TRANS";
      default:
        return p;
    }
  });

  const formatted = replaced.join(" ").toUpperCase();
  return formatted;
};

const TYPE_COLOR_MAP: Record<string, string> = {
  BUNDLE_REACHOUT: "bg-purple-100 text-purple-800",
  PAYMENT_REACHOUT: "bg-orange-100 text-orange-800",
  LAB_REACHOUT: "bg-teal-100 text-teal-800",
};

const formatRecipientInfo = (notification: Notification) => {
  let recipientName = "Unknown";
  let recipientId: string | null = null;

  if (notification.recipient_type === "USER" && notification.user) {
    recipientName = notification.user.full_name;
    recipientId = notification.user.id;
  } else if (
    notification.recipient_type === "PROFESSIONAL" &&
    notification.professional
  ) {
    recipientName = notification.professional.name;
    recipientId = notification.professional.id;
  } else if (notification.recipient_type === "LEAD" && notification.lead) {
    recipientName = notification.lead.full_name;
    recipientId = notification.lead.id;
  }

  return {
    name: recipientName,
    id: recipientId,
    type: notification.recipient_type.toLowerCase(),
  };
};

const NotificationCard = ({
  id,
  notification,
  selectable = false,
  selected = false,
  onSelect,
  hoveredNotificationId,
  setHoveredNotificationId,
  onStatusChange,
  onUpdateTags,
}: NotificationCardProps) => {
  const { region = ALL_REGIONS_CODE } = useParams();
  const recipientInfo = formatRecipientInfo(notification);
  const hasTag = (notification: Notification, tag: string) =>
    notification?.tags?.includes(tag);
  const tags = notification.tags ?? [];

  const { mutate: sendAnyway, isPending: sendingAnyway } =
    useSendNotificationAnyway(onStatusChange);

  const { data: notificationDetails } = useNotification(id!);
  const { session, disparity_check } = notificationDetails || {};

  // "Send Anyway" re-sends this row's message through the server's
  // send-anyway path (sendSessionNotificationWithoutDisparityCheck).
  //
  // Original use: a PROFESSIONAL row parked in PENDING/HOLD because of a
  // disparity mismatch, which care team approves by hand.
  //
  // RH-801: also offered on FAILED rows so care team can re-send after fixing
  // the cause (WATI credits, network blip). Two shapes are safe with the
  // existing server path and nothing else:
  //  - PROFESSIONAL rows of any type: the path re-sends the professional's
  //    message for the session and updates this row.
  //  - Cancellation rows for any recipient: the path dispatches by the row's
  //    type (USER / PARTNER / PROFESSIONAL).
  // USER/PARTNER *booking* rows are deliberately excluded: the path would send
  // the professional's message against them.
  const isCancellation = CANCELLATION_NOTIFICATION_TYPES.includes(
    notification.type
  );
  const awaitingApproval =
    !disparity_check?.is_match &&
    notification.recipient_type === "PROFESSIONAL" &&
    (notification.status === "PENDING" || notification.status === "HOLD");
  const failedAndResendable =
    notification.status === "FAILED" &&
    !!session?.id &&
    (notification.recipient_type === "PROFESSIONAL" || isCancellation);
  const canSendAnyway = awaitingApproval || failedAndResendable;

  const highlight =
    (hoveredNotificationId === notification.id ||
      (hoveredNotificationId !== null &&
        notification.linked_notification_id === hoveredNotificationId)) &&
    notification.recipient_type !== "LEAD";

  const copyPhoneNumber = async (phone: string) => {
    try {
      const sanitizedPhone = phone.replace(/\D/g, "");
      await navigator.clipboard.writeText(sanitizedPhone);
      toast({
        title: "Copied",
        description: "Phone number copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy phone number",
        variant: "destructive",
      });
    }
  };

  return (
    <Card
      className={`w-full mb-2 transition-all border
    ${
      highlight || (selected && notification.recipient_type !== "LEAD")
        ? "border-l-4 border-slate-600 shadow-md"
        : "border-gray-200 border-l-0"
    }`}
    >
      <CardHeader className="pb-0">
        <div className="flex items-start justify-between w-full">
          <div className="flex items-center gap-2">
            {selectable &&
              (notification.status === "HOLD" ||
                notification.status === "PENDING") &&
              notification.recipient_type === "PROFESSIONAL" && (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => onSelect?.(notification, e.target.checked)}
                  className="h-4 w-4 accent-slate-600"
                />
              )}

            {getChannelIcon(notification.channel)}
            <span className="font-medium text-sm">
              {notification.channel} • {recipientInfo.type}
            </span>
            <span
              title={notification.type}
              className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap max-w-[220px] truncate ${
                TYPE_COLOR_MAP[notification.type] ?? "bg-slate-100 text-slate-700"
              }`}
            >
              {formatNotificationType(notification.type)}
            </span>
            {tags?.length > 0 && (
              <div className="flex gap-1">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      TAG_COLOR_MAP[tag] ?? "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {tag.replace("_", " ")}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {getStatusIcon(notification.status)}
              <span
                className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(
                  notification.status
                )}`}
              >
                {notification.status}
              </span>
            </div>
            <HasAccess permission="write">
              {notification.recipient_type === "PROFESSIONAL" &&
                notification.status != "SENT" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex gap-1"
                      >
                        Tags
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      {NOTIFICATION_TAG_OPTIONS.map((option) => {
                        const isActive = hasTag(notification, option.value);

                        return (
                          <DropdownMenuItem
                            key={option.value}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              const newTags = isActive
                                ? tags.filter((t) => t !== option.value)
                                : [...(notification.tags ?? []), option.value];

                              onUpdateTags?.({
                                notificationId: notification.id,
                                tags: newTags,
                              });
                            }}
                            className="flex items-center gap-2"
                          >
                            {isActive && (
                              <Check className="h-3 w-3 text-green-600" />
                            )}
                            {option.label}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              {canSendAnyway && (
                <Button
                  size={"sm"}
                  className="bg-sidebar"
                  onClick={() => {
                    if (session?.id)
                      sendAnyway({
                        sessionId: session.id,
                        notificationId: notification.id,
                      });
                  }}
                  disabled={sendingAnyway}
                  onMouseEnter={() =>
                    setHoveredNotificationId?.(notification.id)
                  }
                  onMouseLeave={() => setHoveredNotificationId?.(null)}
                >
                  {sendingAnyway ? "Sending..." : "Send Anyway"}
                </Button>
              )}
            </HasAccess>
          </div>
        </div>
      </CardHeader>
      <Link to={regionPath(region, `/notifications/${notification.id}`)}>
        <CardContent className="flex flex-col gap-1 px-6 py-2 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-600">Recipient</p>
              {recipientInfo.id && recipientInfo.type !== "professional" ? (
                <Link
                  to={regionPath(region, `/${recipientInfo.type}s/${recipientInfo.id}`)}
                  className="text-blue-500 hover:text-blue-700 transition-colors"
                >
                  {recipientInfo.name}
                </Link>
              ) : (
                <p className="text-sm text-gray-900">{recipientInfo.name}</p>
              )}
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500">
                  {notification.recipient_phone}
                </p>
                {notification.recipient_phone && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyPhoneNumber(notification.recipient_phone);
                    }}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title="Copy phone number"
                  >
                    <Copy className="h-3 w-3 text-gray-500" />
                  </button>
                )}
              </div>
            </div>
            {notification.session && (
              <div>
                <p className="text-xs font-medium text-gray-600">Session</p>
                <Link
                  to={regionPath(region, `/sessions/${notification.session.id}`)}
                  className="text-blue-500 hover:text-blue-700 transition-colors"
                >
                  {notification.session.name}
                </Link>
                <p className="text-xs text-gray-500">
                  {format(new Date(notification.session.start_time), "PPp")}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-600">Template</p>
              <p className="text-sm text-gray-900">
                {notification.template_id || "N/A"}
              </p>
            </div>
          </div>

          {notification.error_message && (
            <div className="mt-1">
              <p className="text-xs text-red-600 bg-red-50 p-1.5 rounded">
                {notification.error_message}
              </p>
            </div>
          )}

          <div className="flex flex-wrap justify-between items-center text-[11px] text-gray-500 pt-2 border-t">
            {notification.sent_at && (
              <span>{format(new Date(notification.sent_at), "PPp")}</span>
            )}
          </div>
        </CardContent>
      </Link>
    </Card>
  );
};

export default NotificationCard;
